/**
 * Step / timeline action slice — Phase 8 commit 82.
 *
 * Owns the step-progression state machine: advanceToStep,
 * completeCurrentStep, setStepStatus, setStepWaiting,
 * completeCurrentStepAndAdvance, setExecutionStep, advanceStep.
 */

/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { StoreApi } from "zustand";
import type { CoAgentState } from "../types";
import {
  appendStepLogEntry,
  clearActiveStepStatuses,
  getAdvancedStepState,
  getMaxStepIndexForType,
  getStepLabel,
  normalizeStepIndex,
} from "../utils";

type SetState = StoreApi<CoAgentState>["setState"];
type GetState = StoreApi<CoAgentState>["getState"];

export type StepActions = Pick<
  CoAgentState,
  | "advanceToStep"
  | "completeCurrentStep"
  | "setStepStatus"
  | "setStepWaiting"
  | "completeCurrentStepAndAdvance"
  | "setExecutionStep"
  | "advanceStep"
>;

export function createStepActions(set: SetState, get: GetState): StepActions {
  return {
    advanceToStep: (stepIndex, status = "waiting") => {
      set((state) => {
        const maxStepIndex = getMaxStepIndexForType(state.coAgentType);
        const from = normalizeStepIndex(state.currentStepIndex, state.coAgentType);
        const target = normalizeStepIndex(stepIndex, state.coAgentType);
        const statuses = clearActiveStepStatuses(state.stepStatuses, maxStepIndex);
        let nextStepLogs = state.stepLogs;

        if (target > 0) {
          statuses[target - 1] = "completed";
        }
        statuses[target] = status;

        if (target !== from) {
          nextStepLogs = appendStepLogEntry(
            nextStepLogs,
            from,
            `Moving on to ${getStepLabel(target, state.coAgentType)}...`,
          );
        }
        nextStepLogs = appendStepLogEntry(
          nextStepLogs,
          target,
          status === "waiting" ? "Waiting for your input" : "Working on it...",
        );

        return {
          currentStepIndex: target,
          focusedStepIndex: null,
          stepStatuses: statuses,
          stepLogs: nextStepLogs,
        };
      });
    },

    completeCurrentStep: () => {
      set((state) => {
        const maxStepIndex = getMaxStepIndexForType(state.coAgentType);
        const current = normalizeStepIndex(state.currentStepIndex, state.coAgentType);
        const next = Math.min(current + 1, maxStepIndex);
        const statuses = clearActiveStepStatuses(state.stepStatuses, maxStepIndex);
        let nextStepLogs = state.stepLogs;

        statuses[current] = "completed";
        if (next > current) {
          statuses[next] = "waiting";
        }

        nextStepLogs = appendStepLogEntry(
          nextStepLogs,
          current,
          `[SUCCESS] ${getStepLabel(current, state.coAgentType)} complete`,
        );
        if (next > current) {
          nextStepLogs = appendStepLogEntry(nextStepLogs, next, `Waiting for your input`);
        }

        return {
          currentStepIndex: next,
          focusedStepIndex: null,
          stepStatuses: statuses,
          stepLogs: nextStepLogs,
        };
      });
    },

    setStepStatus: (stepIndex, status) => {
      const normalized = normalizeStepIndex(stepIndex, get().coAgentType);
      set((state) => ({
        stepStatuses: {
          ...state.stepStatuses,
          [normalized]: status,
        },
        stepLogs: appendStepLogEntry(
          state.stepLogs,
          normalized,
          status === "completed"
            ? "[SUCCESS] Done"
            : status === "waiting"
              ? "Waiting for your input"
              : status === "running"
                ? "Working on it..."
                : `Status: ${status}`,
        ),
      }));
    },

    setStepWaiting: (stepIndex) => {
      get().advanceToStep(stepIndex, "waiting");
    },

    completeCurrentStepAndAdvance: (nextStatus = "running") => {
      set((state) => {
        const current = normalizeStepIndex(state.currentStepIndex, state.coAgentType);
        const advanced = getAdvancedStepState(
          state.currentStepIndex,
          state.stepStatuses,
          nextStatus,
          state.coAgentType,
        );
        let nextStepLogs = appendStepLogEntry(
          state.stepLogs,
          current,
          `[SUCCESS] ${getStepLabel(current, state.coAgentType)} complete`,
        );
        if (advanced.currentStepIndex !== current) {
          nextStepLogs = appendStepLogEntry(
            nextStepLogs,
            advanced.currentStepIndex,
            nextStatus === "waiting"
              ? "Waiting for your input"
              : `Starting ${getStepLabel(advanced.currentStepIndex, state.coAgentType)}...`,
          );
        }
        return {
          ...advanced,
          stepLogs: nextStepLogs,
        };
      });
    },

    setExecutionStep: (stepIndex, status = "running") => {
      get().advanceToStep(stepIndex, status);
    },

    advanceStep: () => {
      get().completeCurrentStepAndAdvance("running");
    },
  };
}
